import { db } from '@/js/firebase'
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore'
import { logger } from './loggerService'
import { DELIVERABLE_STAGES, DEPENDENCY_TYPES } from '@/utils/deliverableUtils'

/**
 * Stages that represent backward/lateral movements — dependency checks are skipped for these.
 * Rationale: you should never be blocked from sending something back for revision.
 */
const BACKWARD_STAGES = new Set([
  DELIVERABLE_STAGES.REVISION_QUEUE,
  DELIVERABLE_STAGES.NOT_READY,
])

/**
 * Stages that mean a dependency is "complete" for review-type dependencies.
 */
const REVIEW_COMPLETE_STAGES = new Set([
  DELIVERABLE_STAGES.APPROVED,
  DELIVERABLE_STAGES.COMPLETE,
])

class DependencyService {
  /**
   * Check if a stage transition is blocked by unmet dependencies.
   *
   * @param {Object} deliverable - Full deliverable document (must include .dependencies)
   * @param {string} targetStage - The stage being transitioned to
   * @returns {Promise<{ blocked: boolean, blockers: Array<{ id: string, title: string, type: string, currentStage: string }> }>}
   */
  async checkDependencies(deliverable, targetStage) {
    const deps = deliverable.dependencies
    if (!deps || deps.length === 0) {
      return { blocked: false, blockers: [] }
    }

    // Backward movements are always allowed
    if (BACKWARD_STAGES.has(targetStage)) {
      return { blocked: false, blockers: [] }
    }

    const blockers = []

    for (const dep of deps) {
      try {
        const depDoc = await getDoc(doc(db, 'deliverables', dep.deliverableId))
        if (!depDoc.exists()) continue

        const depData = { id: depDoc.id, ...depDoc.data() }

        if (!this.isDependencyMet(depData, dep.type, targetStage)) {
          blockers.push({
            id: depData.id,
            title: depData.title || 'Untitled',
            type: dep.type,
            currentStage: depData.stage || 'unknown',
          })
        }
      } catch (err) {
        logger.warn('[DependencyService] Failed to check dependency', {
          deliverableId: deliverable.id,
          dependencyId: dep.deliverableId,
          error: err.message,
        })
      }
    }

    return {
      blocked: blockers.length > 0,
      blockers,
    }
  }

  /**
   * Check if a single dependency is met for a given target stage.
   *
   * - START dependency: met when the dependency has left 'not_ready' (production has begun)
   * - REVIEW dependency: met when the dependency is 'approved' or 'complete'
   *
   * @param {Object} depDeliverable - The dependency deliverable document
   * @param {string} depType - 'start' or 'review'
   * @param {string} targetStage - The stage the parent is trying to move to
   * @returns {boolean}
   */
  isDependencyMet(depDeliverable, depType, targetStage) {
    const depStage = depDeliverable.stage || 'not_ready'

    if (depType === DEPENDENCY_TYPES.START) {
      // Start dependencies only gate movement out of not_ready
      // They are met as long as the dependency itself has started
      return depStage !== DELIVERABLE_STAGES.NOT_READY
    }

    if (depType === DEPENDENCY_TYPES.REVIEW) {
      // Review dependencies gate movement to client_review
      if (targetStage === DELIVERABLE_STAGES.CLIENT_REVIEW) {
        return REVIEW_COMPLETE_STAGES.has(depStage)
      }
      // For other forward stages, review deps don't block
      return true
    }

    return true
  }

  /**
   * Get all deliverables in the linked group within the same project.
   * Uses the explicit linkedDeliverableIds field (bidirectional peer links).
   * Walks transitively: if A links to B and B links to C, all three are in the group.
   *
   * @param {string} deliverableId - Starting deliverable
   * @param {string} projectId - Project to scope the search to
   * @returns {Promise<Array<Object>>} All linked deliverables (including the starting one)
   */
  async getLinkedGroup(deliverableId, projectId) {
    if (!projectId) return []

    const q = query(
      collection(db, 'deliverables'),
      where('projectId', '==', projectId)
    )
    const snapshot = await getDocs(q)
    const allDeliverables = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))

    // Build adjacency from linkedDeliverableIds (already bidirectional by design)
    const adjacency = new Map()
    for (const d of allDeliverables) {
      if (!adjacency.has(d.id)) adjacency.set(d.id, new Set())
      for (const linkedId of (d.linkedDeliverableIds || [])) {
        if (!adjacency.has(linkedId)) adjacency.set(linkedId, new Set())
        adjacency.get(d.id).add(linkedId)
        adjacency.get(linkedId).add(d.id)
      }
    }

    // BFS from the starting deliverable
    const visited = new Set()
    const queue = [deliverableId]
    visited.add(deliverableId)

    while (queue.length > 0) {
      const current = queue.shift()
      const neighbors = adjacency.get(current) || new Set()
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor)
          queue.push(neighbor)
        }
      }
    }

    return allDeliverables.filter(d => visited.has(d.id))
  }
}

export const dependencyService = new DependencyService()
